"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const common_utils_1 = require("@zapier/common-utils");
const design_system_1 = require("@zapier/design-system");
const common_1 = require("../../../common");
const pluralize_1 = __importDefault(require("pluralize"));
const react_1 = require("react");
const date_1 = require("@zapier/date");
const common_components_1 = require("@zapier/common-components");
const theme_1 = require("@zapier/theme");
const constants_1 = require("../../../../constants");
const FloatingMenuAlert_1 = __importDefault(require("../../../FloatingMenuAlert"));
const Styles = common_utils_1.labelStyles(__filename, {
    bodyWrapper: {
        padding: theme_1.Spacing.medium,
    },
    headerWrapper: {
        alignItems: 'center',
        borderBottom: `1px solid ${design_system_1.Colors.grey300}`,
        display: 'flex',
        justifyContent: 'space-between',
        paddingLeft: theme_1.Spacing.medium,
    },
    sampleWrapper: ({ highlighted }) => ({
        backgroundColor: highlighted ? design_system_1.Colors.blue100 : design_system_1.Colors.transparent,
        borderRadius: theme_1.Border.radius.large,
        padding: `${theme_1.Spacing.small}px`,
        display: 'flex',
        alignItems: 'center',
    }),
    sampleInnerWrapper: {
        width: '100%',
    },
    samplesLoadingIndicatorContainer: {
        padding: theme_1.Spacing.small,
    },
    samplesContainer: {
        paddingRight: theme_1.Spacing.extraSmall,
        maxHeight: constants_1.MAX_DROPDOWN_OPTIONS_HEIGHT,
        overflowX: 'hidden',
        overflowY: 'auto',
        '::-webkit-scrollbar': {
            background: 'transparent',
            height: theme_1.Spacing.extraSmall,
            width: theme_1.Spacing.extraSmall,
        },
        '::-webkit-scrollbar-thumb': {
            background: design_system_1.Colors.grey300,
            borderRadius: theme_1.Border.radius.default,
        },
    },
    emptyOptionsContainer: {
        padding: `${theme_1.Spacing.medium}px 0px`,
    },
    buttonContainer: {
        paddingTop: theme_1.Spacing.small,
        display: 'flex',
    },
});
const OptionLabel = ({ item }) => (common_utils_1.isolateComponent(design_system_1.Text, { fontWeight: 600, tag: "p", type: "altText900" }, item.label));
const OptionValue = ({ item }) => {
    if (!item._zap_data_modified_at) {
        return null;
    }
    return (common_utils_1.isolateComponent(design_system_1.Text, { color: "grey500", tag: "p", type: "altText800" },
        "Pulled in ",
        common_utils_1.isolateComponent(date_1.RelativeTime, { date: item._zap_data_modified_at })));
};
const StableOptionValue = react_1.memo(OptionValue);
function SamplePickerItem({ disabled, getItemProps, index, item, checked, groupId, highlighted, setHighlightedIndex, }) {
    const itemProps = getItemProps({ item });
    const onChangeRadio = () => {
        setHighlightedIndex(index);
    };
    const onKeyDown = (event) => {
        if (event.keyCode === common_utils_1.keyCodes.ArrowUp) {
            setHighlightedIndex(index - 1);
        }
        if (event.keyCode === common_utils_1.keyCodes.ArrowDown) {
            setHighlightedIndex(index + 1);
        }
    };
    return (common_utils_1.isolateComponent("div", Object.assign({ css: Styles.sampleWrapper({ highlighted }) }, itemProps, { onKeyDown: onKeyDown, role: "button", tabIndex: -1 }),
        common_utils_1.isolateComponent("div", { css: Styles.sampleInnerWrapper },
            common_utils_1.isolateComponent(common_1.FormLabel, { isDisabled: disabled, layout: "auto-content" },
                common_utils_1.isolateComponent(design_system_1.Radio, { ariaLabel: `Select sample ${item.label}`, checked: Boolean(checked), disabled: disabled, name: groupId, onChange: onChangeRadio, tabIndex: -1, value: String(item._zap_data_sample_id) }),
                common_utils_1.isolateComponent("div", null,
                    common_utils_1.isolateComponent(OptionLabel, { item: item }),
                    common_utils_1.isolateComponent(StableOptionValue, { item: item }))))));
}
const OptionLoadingIndicator = () => (common_utils_1.isolateComponent("div", { css: Styles.samplesLoadingIndicatorContainer },
    common_utils_1.isolateComponent(common_components_1.ContentLoader, { height: "49px" },
        common_utils_1.isolateComponent("circle", { cx: "10", cy: "20", r: "10" }),
        common_utils_1.isolateComponent("rect", { height: "20px", width: "120px", x: "30px", y: "0px" }),
        common_utils_1.isolateComponent("rect", { height: "16px", width: "60px", x: "30px", y: "26px" }))));
const OptionsLoadingIndicator = () => (common_utils_1.isolateComponent("div", { css: Styles.samplesContainer },
    common_utils_1.isolateComponent(OptionLoadingIndicator, { key: "0" })));
const OptionsEmptyIndicator = () => (common_utils_1.isolateComponent("div", { css: Styles.emptyOptionsContainer },
    common_utils_1.isolateComponent(design_system_1.Text, { color: "grey500" }, "No options available.")));
const iconTheme = {
    custom: {
        base: {
            color: design_system_1.Colors.grey700,
        },
        hover: {
            color: design_system_1.Colors.blue500,
        },
        focus: {
            outlineColor: design_system_1.Colors.blue500,
            color: design_system_1.Colors.blue500,
        },
    },
};
function SamplePickerDropdownMenu(props) {
    const { getItemProps, getMenuProps, highlightedIndex, isBuiltInApp, isLoadingMore, isUnableToFindMoreEvents, items, onLoadMore, selectedItem, closeMenu, setHighlightedIndex, sampleNoun, } = props;
    const [hasAttemptedToLoadMore, setHasAttemptedToLoadMore] = react_1.useState(false);
    const groupId = `floating-menu-group-${Math.random()}`;
    const sampleNounInLowercase = sampleNoun.toLowerCase();
    const menuProps = getMenuProps({ 'aria-label': `Samples` });
    const doLoadMore = (e) => {
        e.preventDefault();
        setHasAttemptedToLoadMore(true);
        onLoadMore();
    };
    const doClose = () => {
        closeMenu();
    };
    return (common_utils_1.isolateComponent("div", Object.assign({}, menuProps),
        common_utils_1.isolateComponent("div", { css: Styles.headerWrapper },
            common_utils_1.isolateComponent(design_system_1.Text, { fontWeight: 700 }, "Choose another \u2026"),
            common_utils_1.isolateComponent(design_system_1.IconButton, { color: "custom", colorTheme: iconTheme, icon: "formX", label: "Close", onClick: doClose, size: "small" })),
        isUnableToFindMoreEvents && hasAttemptedToLoadMore && !isLoadingMore && (common_utils_1.isolateComponent(FloatingMenuAlert_1.default, { heading: `We couldn't find any more ${pluralize_1.default(sampleNounInLowercase)}.`, message: `Create a new ${sampleNounInLowercase} ${isBuiltInApp ? '' : 'in your account'} and try again.` })),
        common_utils_1.isolateComponent("div", { css: Styles.bodyWrapper },
            common_utils_1.isolateComponent("div", { css: Styles.samplesContainer },
                items.map((item, index) => (common_utils_1.isolateComponent(SamplePickerItem, { checked: selectedItem &&
                        selectedItem._zap_data_sample_id === item._zap_data_sample_id, disabled: isLoadingMore, getItemProps: getItemProps, groupId: groupId, highlighted: index === highlightedIndex, index: index, item: item, key: index, setHighlightedIndex: setHighlightedIndex }))),
                items.length === 0 &&
                    common_utils_1.cond([isLoadingMore, common_utils_1.isolateComponent(OptionsLoadingIndicator, null)], common_utils_1.isolateComponent(OptionsEmptyIndicator, null))),
            common_utils_1.isolateComponent("div", { css: Styles.buttonContainer },
                common_utils_1.isolateComponent(design_system_1.Button, { color: "greyAlt", disabled: isLoadingMore, isFullWidth: true, onClick: doLoadMore, size: "small" }, isLoadingMore ? 'Loading More …' : 'Load More')))));
}
exports.default = SamplePickerDropdownMenu;
